/**
 * Appointments Page
 *
 * Displays list of appointments with advanced search, filters, and pagination.
 * Uses the EncounterList component for consistent UI/UX with connected mode.
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrganizationStore } from '../stores/organizationStore';
import { EncounterList, UnifiedEncounterItem, EncounterListConfig } from '../components/encounters';
import { AppointmentForm } from '../components/AppointmentForm';
import { TranscriptModal } from '../components/TranscriptModal';
import { ChangeProviderModal } from '../components/encounters/ChangeProviderModal';
import { useAuth } from '../hooks/useAuth';
import { appointmentService } from '../services/appointment';
import { CreateAppointmentData } from '../types/appointment';
import { toast } from '../utils/toast';

const Appointments: React.FC = () => {
  const navigate = useNavigate();
  const { operationMode, canRecord } = useOrganizationStore();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [selectedTranscriptId, setSelectedTranscriptId] = useState<string | null>(null);
  const { user } = useAuth();
  // A standalone admin can hand an appointment to another provider (AMP-137).
  const canChangeProvider = operationMode === 'standalone' && user?.role === 'admin';
  const [providerChange, setProviderChange] = useState<{ item: UnifiedEncounterItem; onChanged: () => void } | null>(null);

  // Handle creating a new appointment
  const handleCreateAppointment = async (data: CreateAppointmentData) => {
    try {
      setCreateLoading(true);
      await appointmentService.createAppointment(data);
      toast.success('Appointment created successfully');
      setShowCreateForm(false);
      // The EncounterList will automatically refresh
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create appointment';
      toast.error(errorMessage);
    } finally {
      setCreateLoading(false);
    }
  };

  // Handle clicking on an appointment
  const handleItemClick = useCallback((item: UnifiedEncounterItem) => {
    navigate(`/appointments/${item.id}`);
  }, [navigate]);

  // Open the recorder. The appointment is marked as recording when capture actually starts, not
  // here: marking it on the way left abandoned visits stuck as "recording".
  const handleStartRecording = useCallback((item: UnifiedEncounterItem) => {
    navigate(`/recording/${item.id}`);
  }, [navigate]);

  // Handle viewing transcript
  const handleViewTranscript = useCallback((item: UnifiedEncounterItem) => {
    setSelectedTranscriptId(item.id);
  }, []);

  // Show create form
  if (showCreateForm) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Create New Appointment</h1>
          <p className="mt-1 text-sm text-gray-600">
            Schedule a new patient appointment for recording and transcription
          </p>
        </div>

        <AppointmentForm
          onSubmit={handleCreateAppointment}
          onCancel={() => setShowCreateForm(false)}
          loading={createLoading}
        />
      </div>
    );
  }

  // Configure the EncounterList component
  const encounterListConfig: EncounterListConfig = {
    mode: 'standalone',
    title: 'Appointments',
    subtitle: 'Manage patient appointments and audio transcriptions',
    // Admins and operators book; staff are read-only (the API refuses them).
    canCreate: operationMode === 'standalone' && (user?.role === 'admin' || user?.role === 'operator'),
    enableSearch: true,
    enableFilters: true,
    enablePagination: true,
    enableInfiniteScroll: true,
    defaultPageSize: 20,
    pageSizeOptions: [10, 20, 50, 100],
    onCreateClick: () => setShowCreateForm(true),
    onViewDetails: handleItemClick,
    // AMP-136: only users who may record get the action; it marks the appointment as recording.
    onStartRecording: canRecord ? handleStartRecording : undefined,
    onViewTranscript: handleViewTranscript,
    onChangeProvider: canChangeProvider ? (item, onChanged) => setProviderChange({ item, onChanged }) : undefined,
  };

  return (
    <div className="space-y-6">
      <EncounterList config={encounterListConfig} />

      {providerChange && (
        <ChangeProviderModal
          item={providerChange.item}
          onChanged={providerChange.onChanged}
          onClose={() => setProviderChange(null)}
        />
      )}

      {/* Transcript Modal */}
      {selectedTranscriptId && (
        <TranscriptModal
          appointmentId={selectedTranscriptId}
          onClose={() => setSelectedTranscriptId(null)}
        />
      )}
    </div>
  );
};

export default Appointments;
